// Middle column — pillbox status grid + combined base status grid + base
// inventory bars. Each status grid is a canvas rendering 16 alliance-
// coloured 12×12 sprites in a 6×3 layout, with a 16×16 type indicator
// filling the centre of row 2. Sprites come from the WinBolo tile atlas
// (tiles.png), per its drawStatusPillbox / drawStatusBase implementations
// [winbolo gui/linux/draw.c:1933+ ; gui/positions.h:87+].
//
// The top slot (formerly an "action area" placeholder) now hosts the bot
// panel — wired directly in main.ts; this module only handles the two
// status grids + the base bars below them.
//
// Layout note: this column does NOT show local-player shell or mine
// inventory — those are bars in the right column. The middle column is for
// global pillbox + base ownership at a glance, plus the closest-base
// inventory bars when the local tank is in BASE_STATUS_RANGE.

import { BASE_DEAD, BASE_FULL_ARMOUR, BASE_FULL_MINES, BASE_FULL_SHELLS, BASE_STATUS_RANGE, WORLD_UNITS_PER_TILE } from '@bolo2k/shared';
import {
  baseStatusKind,
  isFriendlyToMe,
  pillStatusKind,
  type ClientBase,
  type ClientState,
  type ClientTank,
} from '@bolo2k/client-core';
import {
  drawStatusIcon,
  drawStatusIndicator,
  getSprites,
  type LoadedSprites,
  type StatusIconKind,
} from '@bolo2k/client-core';

// Slot-to-grid-position map. Slots 1..16 land in a 6×3 layout with the
// middle 2 cells of row 2 reserved for the indicator. Indices match
// WinBolo positions.h:87-118 / 124-155.
//   row 0 (top):    1  2  3  4  5  6
//   row 1 (middle): 7  8  [indicator x2]  9  10
//   row 2 (bottom): 11 12 13 14 15 16
const SLOT_POSITIONS: ReadonlyArray<readonly [number, number]> = [
  [0, 0], [0, 1], [0, 2], [0, 3], [0, 4], [0, 5],
  [1, 0], [1, 1],                       [1, 4], [1, 5],
  [2, 0], [2, 1], [2, 2], [2, 3], [2, 4], [2, 5],
];

const COLS = 6;
const ROWS = 3;
const GAP_RATIO = 0.18; // visual gap between cells, as fraction of cell size

export class MiddleColumn {
  private pillCtx: CanvasRenderingContext2D;
  private baseCtx: CanvasRenderingContext2D;
  // Base-status bar fills (the 3 horizontal bars below the base grid).
  // Order matches Cheshire's Brain.h struct: shells, mines, armour.
  private baseShellsFill: HTMLElement;
  private baseMinesFill: HTMLElement;
  private baseArmourFill: HTMLElement;
  // Snapshot-version gate: pill/base ownership and base inventory only change
  // on snapshot, so re-rendering at the 60 Hz rAF rate was wasted work. The
  // sprite-loaded flag is part of the gate so the grid repaints once sprites
  // arrive, even if no new snapshot has landed since the !sprites early-skip.
  private lastVersion = -1;
  private lastSpritesLoaded = false;
  // Raster gates: pill/base ownership changes are rare compared to the 25 Hz
  // snapshot rate, so each grid keeps a signature of everything that affects
  // its pixels — canvas dimensions (a ResizeObserver resize clears the
  // canvas, so it must force a repaint), populated slot count, and the
  // per-slot status kinds (which fully encode owner/ally/dead/carried — see
  // pillStatusKind/baseStatusKind). Unchanged signature → skip the raster.
  private lastPillSig = '';
  private lastBaseSig = '';

  constructor(pillCanvas: HTMLCanvasElement, baseCanvas: HTMLCanvasElement) {
    // Status grid canvases — sized adaptively to their slot via
    // ResizeObserver, same pattern Minimap uses. Any actual resize clears
    // the canvas, so it must also drop the raster signature — otherwise an
    // A→B→A dimension bounce between snapshots could leave a blank grid
    // whose signature still matches.
    this.pillCtx = setupGridCanvas(pillCanvas, () => {
      this.lastPillSig = '';
    });
    this.baseCtx = setupGridCanvas(baseCanvas, () => {
      this.lastBaseSig = '';
    });

    // Base-status bar refs (live in static HTML inside the combined
    // base-grid + bars panel).
    const fills = document.querySelectorAll<HTMLElement>('.base-status .base-bar-fill');
    if (fills.length !== 3) throw new Error('expected 3 base-status bar fills');
    this.baseShellsFill = fills[0]!;
    this.baseMinesFill = fills[1]!;
    this.baseArmourFill = fills[2]!;
  }

  draw(state: ClientState): void {
    const sprites = getSprites();
    const spritesLoaded = sprites !== null;
    if (state.version === this.lastVersion && spritesLoaded === this.lastSpritesLoaded) return;
    const me = state.tanks.find((t) => t.id === state.myTankId);

    // --- Status grids (need sprites loaded) ---
    if (sprites) {
      // Pillbox grid
      const pillKinds: StatusIconKind[] = [];
      for (let i = 0; i < 16; i++) {
        const pill = state.pillboxes[i];
        pillKinds.push(pill ? pillStatusKind(pill, state) : 'pillNeutral');
      }
      const pillSig =
        `${this.pillCtx.canvas.width}x${this.pillCtx.canvas.height}|` +
        `${state.pillboxes.length}|${pillKinds.join(',')}`;
      if (pillSig !== this.lastPillSig) {
        renderStatusGrid(this.pillCtx, sprites, pillKinds, 'pill', state.pillboxes.length);
        this.lastPillSig = pillSig;
      }

      // Base grid
      const baseKinds: StatusIconKind[] = [];
      for (let i = 0; i < 16; i++) {
        const base = state.bases[i];
        baseKinds.push(base ? baseStatusKind(base, state, BASE_DEAD) : 'baseNeutral');
      }
      const baseSig =
        `${this.baseCtx.canvas.width}x${this.baseCtx.canvas.height}|` +
        `${state.bases.length}|${baseKinds.join(',')}`;
      if (baseSig !== this.lastBaseSig) {
        renderStatusGrid(this.baseCtx, sprites, baseKinds, 'base', state.bases.length);
        this.lastBaseSig = baseSig;
      }
    }

    // --- Base inventory bars (closest qualifying base) ---
    const closest = me ? closestQualifyingBase(state, me) : null;
    if (closest) {
      setBarWidth(this.baseShellsFill, closest.shells / BASE_FULL_SHELLS);
      setBarWidth(this.baseMinesFill, closest.mines / BASE_FULL_MINES);
      setBarWidth(this.baseArmourFill, closest.armour / BASE_FULL_ARMOUR);
    } else {
      setBarWidth(this.baseShellsFill, 0);
      setBarWidth(this.baseMinesFill, 0);
      setBarWidth(this.baseArmourFill, 0);
    }
    this.lastVersion = state.version;
    this.lastSpritesLoaded = spritesLoaded;
  }
}

function setupGridCanvas(
  canvas: HTMLCanvasElement,
  onResize?: () => void,
): CanvasRenderingContext2D {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('status-grid 2d context unavailable');
  const fit = () => {
    const r = canvas.getBoundingClientRect();
    const w = Math.max(1, Math.floor(r.width));
    const h = Math.max(1, Math.floor(r.height));
    if (canvas.width === w && canvas.height === h) return;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    onResize?.();
  };
  fit();
  new ResizeObserver(fit).observe(canvas);
  return ctx;
}

function renderStatusGrid(
  ctx: CanvasRenderingContext2D,
  sprites: LoadedSprites,
  cells: ReadonlyArray<StatusIconKind>,
  indicator: 'pill' | 'base',
  populated: number,
): void {
  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);

  // Solve for a cell size that fits 6 cols × 3 rows with proportional gaps
  // and a small panel padding. Cells stay square so the sprites don't
  // distort.
  const padding = 4;
  const usableW = Math.max(1, w - padding * 2);
  const usableH = Math.max(1, h - padding * 2);
  // Width-constrained: cellW + gapX = usableW / 6 ; gapX = cellW * GAP_RATIO
  // → cellW * (1 + GAP_RATIO) ≈ usableW / 6
  const cellWByWidth = usableW / (COLS + (COLS - 1) * GAP_RATIO);
  const cellHByHeight = usableH / (ROWS + (ROWS - 1) * GAP_RATIO);
  const cellSize = Math.floor(Math.min(cellWByWidth, cellHByHeight));
  if (cellSize < 4) return; // canvas too small to render anything useful
  const gap = Math.max(1, Math.floor(cellSize * GAP_RATIO));
  const gridW = COLS * cellSize + (COLS - 1) * gap;
  const gridH = ROWS * cellSize + (ROWS - 1) * gap;
  const offsetX = Math.floor((w - gridW) / 2);
  const offsetY = Math.floor((h - gridH) / 2);

  // 16 cells. Slots beyond `populated` render as empty (skip them).
  for (let i = 0; i < 16; i++) {
    if (i >= populated) continue;
    const [row, col] = SLOT_POSITIONS[i]!;
    const dx = offsetX + col * (cellSize + gap);
    const dy = offsetY + row * (cellSize + gap);
    drawStatusIcon(ctx, sprites, cells[i]!, dx, dy, cellSize);
  }

  // Indicator spans cols 2-3 of row 1, occupying the middle area. Keep it
  // square (= cellSize) and centre it horizontally across the 2-cell span.
  const indSize = cellSize;
  const indSpanW = 2 * cellSize + gap;
  const indDx = offsetX + 2 * (cellSize + gap) + Math.floor((indSpanW - indSize) / 2);
  const indDy = offsetY + 1 * (cellSize + gap);
  drawStatusIndicator(ctx, sprites, indicator, indDx, indDy, indSize);
}

function closestQualifyingBase(state: ClientState, me: ClientTank): ClientBase | null {
  let closest: ClientBase | null = null;
  let bestDistSq = BASE_STATUS_RANGE * BASE_STATUS_RANGE;
  for (const base of state.bases) {
    const isOwn = base.owner === me.owner;
    const isNeutral = base.owner === 0xff;
    const isAlly = !isOwn && !isNeutral && isFriendlyToMe(state, base.owner);
    if (!isOwn && !isAlly && !isNeutral) continue;
    const half = WORLD_UNITS_PER_TILE >> 1;
    const baseX = (base.x << 8) + half;
    const baseY = (base.y << 8) + half;
    const dx = me.x - baseX;
    const dy = me.y - baseY;
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      closest = base;
    }
  }
  return closest;
}

function setBarWidth(el: HTMLElement, fraction: number): void {
  const pct = Math.max(0, Math.min(1, fraction)) * 100;
  const w = `${pct.toFixed(1)}%`;
  if (el.style.width !== w) el.style.width = w;
}
